import { AlertTriangle, Info, Lightbulb, Sparkles } from 'lucide-react';

import { cn } from '../../utils/cn';

export type ArticleCalloutType = 'note' | 'tip' | 'important' | 'warning' | 'definition' | 'tldr';

interface CalloutConfig {
	icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
	/** icon color only — no container/box */
	color: string;
}

const CALLOUT_CONFIG: Record<Exclude<ArticleCalloutType, 'definition' | 'tldr'>, CalloutConfig> = {
	note: { icon: Info, color: 'text-accent' },
	tip: { icon: Lightbulb, color: 'text-accent' },
	important: { icon: Sparkles, color: 'text-accent' },
	warning: { icon: AlertTriangle, color: 'text-warning' },
};

/** Shared prose styling for a callout's body so nested markdown reads correctly. */
const BODY_PROSE =
	'min-w-0 flex-1 text-[15.5px] leading-[1.65] text-neutral/60 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:text-accent [&_a]:underline [&_code]:rounded [&_code]:bg-neutral/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em] [&_strong]:font-semibold [&_strong]:text-neutral [&_p]:mb-2';

/**
 * Summary card body: the bullets are the whole point, so the list hugs the
 * label and reads at full contrast.
 */
const TLDR_BODY = cn(
	'text-neutral/80',
	// The stripped `[!TLDR]` marker line leaves an empty leading <p>.
	'[&>p:empty]:hidden',
	'[&_ul]:mb-0 [&_ul]:ml-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-neutral/80',
	'[&_ul>li]:pl-1',
);

/**
 * Dictionary-entry overrides for the definition card body: near-white senses,
 * bold dictionary numbers on ordered lists, and a small muted final line (the
 * attribution / "first named" note).
 */
const DEFINITION_BODY = cn(
	// The markdown renderer puts its own utilities directly on the <ol>/<p>;
	// these descendant variants out-specify them (class + element selector).
	'text-neutral/85',
	// The stripped `[!DEFINITION]` marker line leaves an empty leading <p>.
	'[&>p:empty]:hidden',
	'[&_ol]:mb-0 [&_ol]:ml-0 [&_ol]:mt-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:text-neutral/85',
	'[&_ol>li]:mb-1.5 [&_ol>li]:pl-1.5 [&_ol>li]:marker:font-bold [&_ol>li]:marker:text-neutral',
	'[&>p:last-child]:mt-3 [&>p:last-child]:text-[13px] [&>p:last-child]:leading-[1.5] [&>p:last-child]:text-neutral/50',
);

/**
 * Parse a definition title of the form `term |pronunciation| · pos` into its
 * dictionary-entry parts. Every segment after the headword is optional, so
 * plain titles ("the filing tax") keep rendering.
 */
function parseDefinitionTitle(title?: string): {
	headword?: string;
	pronunciation?: string;
	pos?: string;
} {
	if (!title) return {};
	const [head, pos] = title.split('·').map((s) => s.trim());
	const pron = head?.match(/\|[^|]+\|/)?.[0];
	const headword = pron ? head.replace(pron, '').trim() : head;
	return { headword, pronunciation: pron, pos };
}

/**
 * ArticleCallout — shared callout for the docs and blog renderers.
 *
 * `note` / `tip` / `important` / `warning` render a left-border rule (like a
 * blockquote) with a colored leading icon. `definition` renders a filled accent
 * card with an uppercase label — used for inline term definitions. `tldr`
 * renders the article's Summary (TL;DR) card: a filled surface with a literal
 * "Summary (TL;DR)" label so readers and AI crawlers get the gist without
 * parsing the page. An optional `title` appends to the label (e.g.
 * "Definition · the filing tax").
 */
export const ArticleCallout = ({
	type,
	title,
	children,
}: {
	type: ArticleCalloutType;
	title?: string;
	children: React.ReactNode;
}): React.JSX.Element => {
	if (type === 'definition') {
		// Dictionary-entry card: accent surface, headword + |pronunciation| + part
		// of speech, then numbered senses (white text) and a muted attribution line.
		const { headword, pronunciation, pos } = parseDefinitionTitle(title);
		return (
			<div className="my-[26px] rounded-xl bg-accent/[0.12] px-6 py-5">
				<div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
					Definition
				</div>
				{headword && (
					<div className="flex flex-wrap items-baseline gap-x-2.5">
						<span className="text-[22px] font-bold leading-[1.2] tracking-[-0.01em] text-neutral">
							{headword}
						</span>
						{pronunciation && (
							<span className="text-[16px] font-light text-neutral/50">{pronunciation}</span>
						)}
					</div>
				)}
				{pos && <div className="mt-0.5 text-[14px] italic leading-[1.4] text-neutral/50">{pos}</div>}
				<div className={cn(BODY_PROSE, DEFINITION_BODY, 'mt-2.5')}>{children}</div>
			</div>
		);
	}

	if (type === 'tldr') {
		return (
			<aside
				aria-label="Summary (TL;DR)"
				className="my-[26px] rounded-lg border border-border-weak bg-surface px-6 py-5"
			>
				<p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral/50">
					Summary (TL;DR)
				</p>
				<div className={cn(BODY_PROSE, TLDR_BODY)}>{children}</div>
			</aside>
		);
	}

	const config = CALLOUT_CONFIG[type];
	const Icon = config.icon;

	return (
		<div className="my-[26px] flex gap-3 border-l-2 border-neutral/10 pl-4">
			<Icon className={cn('mt-[3px] size-[18px] shrink-0', config.color)} strokeWidth={2} />
			<div className={BODY_PROSE}>
				{title ? <p className="mb-2 font-semibold text-neutral">{title}</p> : null}
				{children}
			</div>
		</div>
	);
};
